import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const LOGO_URL = "https://equiprwebsitestorage.blob.core.windows.net/website-assets/EQUIPR%20Icon300X300px.png";

const notifications = [
  // Australia
  { city: 'Melbourne', action: 'booked a demo', product: 'EQUIPR Core' },
  { city: 'Sydney', action: 'requested a demo', product: 'EQUIPR Core' },
  { city: 'Brisbane', action: 'booked a demo', product: 'EQUIPR Core' },
  { city: 'Perth', action: 'booked a demo', product: 'EQUIPR Guardian' },
  { city: 'Adelaide', action: 'booked a demo', product: 'EQUIPR Core' },
  { city: 'Gold Coast', action: 'requested a demo', product: 'EQUIPR Core' },
  { city: 'Newcastle', action: 'booked a demo', product: 'EQUIPR Core' },
  { city: 'Townsville', action: 'requested a demo', product: 'EQUIPR Core' },
  { city: 'Darwin', action: 'booked a demo', product: 'EQUIPR Guardian' },
  { city: 'Hobart', action: 'requested a demo', product: 'EQUIPR Core' },
  { city: 'Cairns', action: 'booked a demo', product: 'EQUIPR Core' },
  { city: 'Geelong', action: 'booked a demo', product: 'EQUIPR Core' },
  { city: 'Mackay', action: 'requested a demo', product: 'EQUIPR Core' },
  { city: 'Sunshine Coast', action: 'booked a demo', product: 'EQUIPR Core' },
  { city: 'Wollongong', action: 'requested a demo', product: 'EQUIPR Core' },
  { city: 'Bunbury', action: 'booked a demo', product: 'EQUIPR Core' },
  { city: 'Rockhampton', action: 'requested a demo', product: 'EQUIPR Core' },
  { city: 'Canberra', action: 'booked a demo', product: 'EQUIPR Core' },
  // New Zealand
  { city: 'Auckland', action: 'booked a demo', product: 'EQUIPR Core' },
  { city: 'Christchurch', action: 'requested a demo', product: 'EQUIPR Core' },
  { city: 'Wellington', action: 'booked a demo', product: 'EQUIPR Core' },
  { city: 'Hamilton', action: 'requested a demo', product: 'EQUIPR Guardian' },
  { city: 'Tauranga', action: 'booked a demo', product: 'EQUIPR Core' },
  // United Kingdom
  { city: 'London', action: 'booked a demo', product: 'EQUIPR Core' },
  { city: 'Manchester', action: 'requested a demo', product: 'EQUIPR Core' },
  { city: 'Birmingham', action: 'booked a demo', product: 'EQUIPR Core' },
  { city: 'Leeds', action: 'requested a demo', product: 'EQUIPR Guardian' },
  { city: 'Glasgow', action: 'booked a demo', product: 'EQUIPR Core' },
  { city: 'Edinburgh', action: 'requested a demo', product: 'EQUIPR Core' },
  { city: 'Bristol', action: 'booked a demo', product: 'EQUIPR Core' },
  { city: 'Cardiff', action: 'requested a demo', product: 'EQUIPR Core' },
  // United States
  { city: 'Houston', action: 'booked a demo', product: 'EQUIPR Core' },
  { city: 'Dallas', action: 'requested a demo', product: 'EQUIPR Core' },
  { city: 'Phoenix', action: 'booked a demo', product: 'EQUIPR Core' },
  { city: 'Denver', action: 'requested a demo', product: 'EQUIPR Guardian' },
  { city: 'Atlanta', action: 'booked a demo', product: 'EQUIPR Core' },
  { city: 'Chicago', action: 'requested a demo', product: 'EQUIPR Core' },
  { city: 'Nashville', action: 'booked a demo', product: 'EQUIPR Core' },
  { city: 'Charlotte', action: 'requested a demo', product: 'EQUIPR Core' },
];

const timeAgoOptions = [
  '2 minutes ago',
  '5 minutes ago',
  '8 minutes ago',
  '12 minutes ago',
  '18 minutes ago',
  '25 minutes ago',
  '32 minutes ago',
  '1 hour ago',
  '2 hours ago',
  '3 hours ago',
];

const SocialProofToast = () => {
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const showNext = useCallback(() => {
    if (dismissed) return;
    const notif = notifications[Math.floor(Math.random() * notifications.length)];
    const timeAgo = timeAgoOptions[Math.floor(Math.random() * timeAgoOptions.length)];
    setCurrent({ ...notif, timeAgo });
    setVisible(true);

    // Auto-hide after 10 seconds
    setTimeout(() => setVisible(false), 10000);
  }, [dismissed]);

  useEffect(() => {
    // First notification after 45 seconds
    const initialTimer = setTimeout(() => showNext(), 45000);

    // Then every 120-240 seconds
    const interval = setInterval(() => {
      showNext();
    }, 120000 + Math.random() * 120000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [showNext]);

  if (dismissed || !current) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, x: -80, y: 0 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: -80 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          className="fixed bottom-24 left-5 z-40 max-w-xs"
          data-testid="social-proof-toast"
        >
          <div className="bg-white rounded-lg shadow-xl border border-gray-100 p-3.5 flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-equipr-dark flex items-center justify-center overflow-hidden">
              <img src={LOGO_URL} alt="" className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-gray-900 text-sm leading-snug">
                <span className="font-semibold">A company from {current.city}</span>{' '}
                {current.action} of{' '}
                <span className="font-semibold text-equipr-dark">{current.product}</span>
              </p>
              <p className="text-gray-400 text-xs mt-1">{current.timeAgo}</p>
            </div>
            <button
              onClick={() => { setVisible(false); setDismissed(true); }}
              className="flex-shrink-0 text-gray-300 hover:text-gray-500 transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SocialProofToast;
